import React, { useEffect, useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { getRestaurantById } from "../../../api/restaurants.api";
import { baseURL } from "../../../api/api";
import { RotatingLines } from "react-loader-spinner";

const RestaurantDetailsModal = ({ isOpen, onClose, restaurantId }) => {
  const [restaurantDetails, setRestaurantDetails] = useState(null);
  const [currentImage, setCurrentImage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && restaurantId) {
      setLoading(true);
      getRestaurantById(restaurantId)
        .then((res) => {
          setRestaurantDetails(res.data);
          setCurrentImage(res.data?.images?.[0] || "");
        })
        .catch((err) =>
          console.error("Error fetching restaurant details:", err)
        )
        .finally(() => setLoading(false));
    }
  }, [isOpen, restaurantId]);

  const handleImageClick = (image) => {
    setCurrentImage(image);
  };

  const getImageUrl = (imageName) => `${baseURL}/s3/images/${imageName}`;

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="bg-white p-6 rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-2xl"
              >
                &times;
              </button>

              {/* Loader */}
              {loading ? (
                <div className="flex justify-center items-center h-60">
                  <RotatingLines
                    strokeColor="gray"
                    strokeWidth="5"
                    animationDuration="0.75"
                    width="50"
                    visible={true}
                  />
                </div>
              ) : (
                <>
                  {/* Restaurant Title */}
                  <h2 className="text-xl font-semibold text-gray-700">
                    {restaurantDetails?.name || "Restaurant Details"}
                  </h2>

                  {/* Large Image Display */}
                  {currentImage && (
                    <div className="flex justify-center items-center h-80 mb-4">
                      <img
                        src={getImageUrl(currentImage)}
                        alt="Current"
                        className="max-h-full max-w-full object-contain rounded-lg shadow-lg"
                      />
                    </div>
                  )}

                  {/* Thumbnails */}
                  <div className="flex justify-center space-x-4">
                    {restaurantDetails?.images?.map((image, index) => (
                      <img
                        key={index}
                        src={getImageUrl(image)}
                        alt={`Thumbnail ${index + 1}`}
                        className={`w-20 h-20 object-cover cursor-pointer rounded-md shadow-md ${
                          currentImage === image
                            ? "border-2 border-blue-500"
                            : ""
                        } hover:opacity-75`}
                        onClick={() => handleImageClick(image)}
                      />
                    ))}
                  </div>

                  {/* Details Section */}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p>
                        <strong>Location:</strong>{" "}
                        {restaurantDetails?.location || "N/A"}
                      </p>
                      <p>
                        <strong>Seating Capacity:</strong>{" "}
                        {restaurantDetails?.seatingCapacity || "N/A"}
                      </p>
                      <p>
                        <strong>Foot Traffic:</strong>{" "}
                        {restaurantDetails?.footTrafficEstimate || "N/A"}
                      </p>
                      <p>
                        <strong>Price Per Week :</strong>{" "}
                        {restaurantDetails?.basePricePerWeek || "10"}€  
                      </p>
                    </div>

                    {/* Contact Info */}
                    <div>
                      <p>
                        <strong>Phone:</strong>{" "}
                        {restaurantDetails?.contactInformation?.phone || "N/A"}
                      </p>
                      {restaurantDetails?.contactInformation?.email && (
                        <p>
                          <strong>Email:</strong>{" "}
                          {restaurantDetails?.contactInformation?.email}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  {restaurantDetails?.description && (
                    <div className="mt-4">
                      <h3 className="text-lg font-semibold">Description</h3>
                      <p>{restaurantDetails.description}</p>
                    </div>
                  )}
                </>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default RestaurantDetailsModal;
